<template>
    <div>
        <v-app>
            <md-snackbar :md-position="snackbar.position"
                :md-duration="snackbar.duration"
                :md-active.sync="snackbar.visibleSnackbar"
                :md-persistent="false">
                <span>{{ snackbar.alertMessage }}</span>
                <md-button class="md-primary"
                    @click="snackbar.visibleSnackbar = false">
                    {{ $t('actions.dismiss') }}
                </md-button>
            </md-snackbar>

            <md-toolbar>
                <!--Left search -->
                <div class="md-toolbar-section-start">
                    <div class="legend">
                        <span class="confirmed">{{ $t('general.active') }}</span>
                        <span class="rejected">{{ $t('general.inactive') }}</span>
                    </div>
                </div>
                <!--Right search -->
                <div class="md-toolbar-section-end">
                    <md-field md-clearable>
                        <md-input :placeholder="$t('general.search-by-name')"
                            append-icon="search"
                            v-model="search">
                        </md-input>
                    </md-field>
                    <md-field>
                        <label for="movie">{{ $tc('products.status') }}</label>
                        <md-select v-model="active"
                            name="activeStatus"
                            id="activeStatus"
                            @input="fetchUsers">
                            <md-option value="all">
                                {{ $t('general.all') }}
                            </md-option>
                            <md-option value="1">
                                {{ $t('general.active') }}
                            </md-option>
                            <md-option value="0">
                                {{ $t('general.inactive') }}
                            </md-option>
                        </md-select>
                    </md-field>
                    <md-button class="md-icon-button"
                        @click="fetchUsers">
                        <md-icon>refresh</md-icon>
                    </md-button>
                    <md-button class="md-icon-button"
                        @click="addUser">
                        <md-icon>add_circle_outline</md-icon>
                    </md-button>
                </div>
            </md-toolbar>

            <div>
                <md-progress-spinner v-if="isLoading"
                    md-mode="indeterminate"></md-progress-spinner>

                <v-data-table v-else
                    :headers="headers"
                    :items="users"
                    :pagination.sync="pagination"
                    class="elevation-1"
                    rows-per-page-text="$vuetify.dataIterator.rowsPerPageText"
                    no-results-text="$vuetify.noDataText"
                    no-data-text="$vuetify.noDataText"
                    :rows-per-page-items="paginationAux.rowsPerPageItems"
                    :total-items="paginationAux.totalItems">
                    <template v-slot:items="props">
                        <tr :class="{inactiveRecord: !props.item.active, activeRecord: props.item.active}">
                            <td class="text-xs-center">
                                {{ props.item.user_id }}
                            </td>
                            <td class="text-xs-left">
                                {{ props.item.first_name }}
                            </td>
                            <td class="text-xs-left">
                                {{ props.item.last_name }}
                            </td>
                            <td class="text-xs-center">
                                {{ props.item.email }}
                            </td>
                            <td class="text-xs-center">
                                {{ props.item.phone }}
                            </td>
                            <td class="text-xs-center">
                                {{ props.item.role_name }}
                            </td>
                            <td class="text-xs-center">
                                {{ formatDateTime(props.item.created_at) }}
                            </td>
                            <td class="text-xs-right">
                                {{ isActive(props.item.active) }}
                            </td>
                            <td class="justify-center px-0">
                                <md-button class="md-icon-button"
                                    @click="editUser(props.item)"
                                    :disabled="!props.item.active">
                                    <md-icon>edit</md-icon>
                                    <md-tooltip md-direction="top">
                                        {{ $t('actions.edit') }}
                                    </md-tooltip>
                                </md-button>
                                <md-button class="md-icon-button"
                                    v-if="!props.item.active"
                                    @click="restoreUser(props.item)">
                                    <md-icon>check_circle</md-icon>
                                    <md-tooltip md-direction="top">
                                        {{ $t('actions.enable') }}
                                    </md-tooltip>
                                </md-button>
                                <md-button class="md-icon-button"
                                    v-else
                                    @click="deleteUser(props.item)">
                                    <md-icon>delete</md-icon>
                                    <md-tooltip md-direction="top">
                                        {{ $t('actions.disable') }}
                                    </md-tooltip>
                                </md-button>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </div>
        </v-app>
    </div>
</template>

<script>
import _ from 'lodash'
import usersService from '@/components/users/services/users'
import Swal from 'sweetalert2'
import luxon from '@/mixins/luxon'

const loadingKeys = {
    creatingItem: 'creating-item',
    fetchingItem: 'fetching-item'
}

export default {
    name: 'OrganizationList',

    mixins: [luxon],

    components: {
    },

    data () {
        return {
            search: '',

            users: [],

            loadings: [],

            active: '1',

            snackbar: {
                visibleSnackbar: false,
                position: 'center',
                duration: 3000,
                alertMessage: this.$t(
                    'validations.operation-performed-successfully'
                )
            },

            paginationAux: {
                descending: true,
                page: 1,
                rowsPerPage: 10,
                sortBy: 'user_id',
                totalItems: 0,
                rowsPerPageItems: [5, 10, 15, 25, 50]
            },

            headers: [
                {
                    text: this.$t('general.id'),
                    align: 'center',
                    value: 'user_id',
                    sortable: true
                },
                {
                    text: this.$t('users.first-name'),
                    align: 'left',
                    value: 'first_name',
                    sortable: true
                },
                {
                    text: this.$t('users.last-name'),
                    align: 'left',
                    value: 'last_name',
                    sortable: false
                },
                {
                    text: this.$t('users.email'),
                    align: 'center',
                    value: 'email',
                    sortable: false
                },
                {
                    text: this.$t('users.phone'),
                    align: 'center',
                    value: 'phone',
                    sortable: false
                },
                {
                    text: this.$t('users.role'),
                    align: 'center',
                    value: 'rolename',
                    sortable: false
                },
                {
                    text: this.$t('general.created-at'),
                    align: 'center',
                    value: 'created_at',
                    sortable: false
                },
                {
                    text: this.$t('general.active'),
                    align: 'right',
                    value: 'active',
                    sortable: false
                },
                {
                    text: 'Actions',
                    align: 'center',
                    sortable: false
                }
            ]
        }
    },
    computed: {
        pagination: {
            get: function () {
                return this.paginationAux
            },
            set: function (value) {
                this.paginationAux = value
                this.fetchUsers()
            }
        },

        isLoading () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        }
    },

    methods: {
        async fetchUsers () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            self.users = []
            let params = {
                pagination: {
                    page: self.paginationAux.page,
                    rowsPerPage: self.paginationAux.rowsPerPage
                },
                orderBy: {
                    column: self.paginationAux.sortBy,
                    direction: self.paginationAux.descending ? 'DESC' : 'ASC'
                },
                filter: {
                    active: self.active === 'all' ? undefined : self.active,
                    organization_id: self.$route.params.id,
                    first_name: self.search,
                    last_name: self.search,
                    email: self.search
                }
            }

            let response = await usersService.getAll(params)
            self.users = _.cloneDeep(response.data.results)

            self.paginationAux.totalItems = response.data.pagination.totalItems
            self.paginationAux.rowsPerPage = response.data.pagination.rowsPerPage
            self.paginationAux.currentPage = response.data.pagination.currentPage

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        addUser () {
            let organizationId = this.$route.params.id
            this.$router.push('/organizations/' + organizationId + '/new-user')
        },

        editUser (item) {
            let organizationId = this.$route.params.id
            this.$router.push('/organizations/' + organizationId + '/edit-user/' + item.user_id)
        },

        deleteUser (item) {
            Swal.fire({
                title: this.$t('validations.confirmation'),
                text: this.$t('validations.question-item-delete-confirm'),
                type: 'warning',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonText: this.$t('actions.cancel'),
                confirmButtonText: this.$t('actions.confirm'),
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.confirmDelete(item)
                }
            })
        },

        async confirmDelete (item) {
            let self = this
            let parameters = {
                'user_id': item.user_id
            }

            await usersService.delete(parameters)

            self.fetchUsers()
            self.showSnackbar()
        },

        restoreUser (item) {
            Swal.fire({
                title: this.$t('validations.confirmation'),
                text: this.$t('validations.question-item-restore-confirm'),
                type: 'warning',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonText: this.$t('actions.cancel'),
                confirmButtonText: this.$t('actions.confirm'),
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.confirmRestore(item)
                }
            })
        },

        async confirmRestore (item) {
            let self = this
            let parameters = {
                'user_id': item.user_id
            }

            await usersService.restore(parameters)

            self.fetchUsers()
            self.showSnackbar()
        },

        showSnackbar () {
            this.snackbar.visibleSnackbar = true
        },

        isActive (value) {
            return value === true ? 'Sim' : 'Não'
        }
    },

    created () {
        this.fetchUsers()
    }
}
</script>
